import {
    AutocompleteChangeReason,
    AutocompleteRenderOptionState,
    Box,
    FormControlLabel,
    Stack,
    Switch,
    Theme,
} from '@mui/material';
import {
    ChipAutocomplete,
    PoolButton,
    PoolModal,
    PoolTextArea,
    PoolTypography,
    ScrollToFieldError,
    SectionDivider,
} from '@synergy/components';
import { Classification, Tag } from '@synergy/models';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloFormSection, PaloLinkRow } from '@synergy/components-palo';
import { SubPageHandle, fields, useFormikSubPage } from '@synergy/utils';
import { SyntheticEvent, forwardRef, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useLocation, useNavigate } from 'react-router';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import * as SupplierDetailOverviewActions from './store/supplier-detail-overview-actions';
import * as TagsActions from '../../../../../common/actions/tag-actions';
import { ClassificationAutocomplete } from '../../../../../common/components/classification-autocomplete/classification-autocomplete';
import { EmailTable } from '../../../components/emails/emails-table';
import { InternalNotes } from '../../../../../common/components/internal-notes/internal-notes';
import { NoteType } from '../../../../../common/models/enums/note-type';
import { PaloAccountHead } from '../../../components/palo-account-head';
import { PaloDirtyComparison } from '../../../../../common/components/palo/palo-dirty-context';
import { PaloSectionText } from '../../../../../common/components/palo/palo-section-text';
import { PaloSwitchLabel } from '../../../../../common/components/palo/palo-switch-label';
import { ParentSupplierAutocompleteList } from './components/parent-supplier-autocomplete-list';
import { PhonesTable } from '../../../components/phones/phones-table';
import { SupplierDetailOverviewBankAccounts } from './components/supplier-detail-overview-bank-accounts';
import { SupplierDetailOverviewBasicInfos } from './components/supplier-detail-overview-basic-infos';
import { SupplierDetailOverviewCategories } from './components/supplier-detail-overview-categories';
import { SupplierDetailOverviewFormValues } from './models/supplier-detail-overview-form-values';
import { SupplierDetailOverviewNotifications } from './components/supplier-detail-overview-notifications';
import { SupplierOverviewDetailSectionFtp } from './components/supplier-detail-overview-section-ftp';
import { SupplierOverviewWhitelist } from './components/supplier-detail-overview-whitelist';
import {
    supplierDetailOverviewBaselineSelector,
    supplierDetailOverviewFormDataSelector,
    supplierSelector,
} from './store/supplier-detail-overview-selectors';
import { supplierDetailOverviewValidationSchema } from './supplier-detail-overview-validation-schema';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    // No gap of its own: every child is a PaloFormSection and brings the 20
    // pixels the rest of the app puts between sections. With both, the mask
    // ran 68 pixels of nothing between one heading and the next.
    container: {
        display: 'flex',
        flexDirection: 'column',
    },
    highlight: {
        color: theme.palette.primary.main,
    },
    toggleContainer: {
        marginBottom: -10,
        position: 'absolute',
        right: 0,
    },
    headerWithToggle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
}));

/** The addresses are compared on their own tab (#2261). */
const withoutAddresses = (values?: SupplierDetailOverviewFormValues) => {
    if (!values) {
        return undefined;
    }

    const { addresses: _, ...rest } = values;

    return rest;
};

export const SupplierDetailOverview = forwardRef<SubPageHandle, Record<string, unknown>>((_, ref) => {
    const classes = useStyles();
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const fieldNames = fields<SupplierDetailOverviewFormValues>();

    const tags = useAppSelector(state => state.tag.tags);
    const supplier = useAppSelector(supplierSelector);
    const formikRef = useRef<FormikProps<SupplierDetailOverviewFormValues>>(null);
    const { formValues, errors, touched } = useAppSelector(supplierDetailOverviewFormDataSelector);
    // The supplier as the server has it — see PaloDirtyComparison.
    const baseline = useAppSelector(supplierDetailOverviewBaselineSelector);
    const [showTagsDeleteModal, setShowTagsDeleteModal] = useState(false);
    const [showClassificationDeleteModal, setShowClassificationDeleteModal] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    // The addresses have a tab of their own beside this one (#2261).
    const showAddresses = () => navigate(location.pathname.replace(/[^/]+$/, 'addresses'));

    useFormikSubPage(ref, formikRef, (values, isDirty, isValid, errs) => {
        dispatch(SupplierDetailOverviewActions.updateSupplierDetail(values, isDirty, isValid, errs));
    });

    const handleTagsChange =
        (formikProps: FormikProps<SupplierDetailOverviewFormValues>) =>
        (_event: SyntheticEvent<Element, Event>, values: Tag[], reason: AutocompleteChangeReason) => {
            if (reason === 'clear') {
                setShowTagsDeleteModal(true);
            } else {
                formikProps.setFieldValue(fieldNames.tags, values);
            }
        };

    const handleTagsDelete = (formikProps: FormikProps<SupplierDetailOverviewFormValues>) => {
        formikProps.setFieldValue(fieldNames.tags, []);
        setShowTagsDeleteModal(false);
    };

    const handleClassificationChange =
        (formikProps: FormikProps<SupplierDetailOverviewFormValues>) =>
        (_event: SyntheticEvent, values: Classification[], reason: AutocompleteChangeReason) => {
            if (reason === 'clear') {
                setShowClassificationDeleteModal(true);
            } else {
                formikProps.setFieldValue(fieldNames.classifications, values);
            }
        };

    const handleClassificationsDelete = (formikProps: FormikProps<SupplierDetailOverviewFormValues>) => {
        formikProps.setFieldValue(fieldNames.classifications, []);
        setShowClassificationDeleteModal(false);
    };

    const getTagsDeleteModalActionChildren = (formikProps: FormikProps<SupplierDetailOverviewFormValues>) => {
        return [
            <PoolButton key="2" variant="text" styleType="firstLevel" onClick={() => setShowTagsDeleteModal(false)}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton key="1" styleType="firstLevel" color="secondary" onClick={() => handleTagsDelete(formikProps)}>
                <FormattedMessage id="app.common.delete" />
            </PoolButton>,
        ];
    };

    const getClassificationsModalActionChildren = (formikProps: FormikProps<SupplierDetailOverviewFormValues>) => {
        return [
            <PoolButton
                key="2"
                variant="text"
                styleType="firstLevel"
                onClick={() => setShowClassificationDeleteModal(false)}
            >
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton
                key="1"
                styleType="firstLevel"
                color="secondary"
                onClick={() => handleClassificationsDelete(formikProps)}
            >
                <FormattedMessage id="app.common.delete" />
            </PoolButton>,
        ];
    };

    const renderOption = (item: Tag, state: AutocompleteRenderOptionState) => {
        const nameMatches = match(item.name, state.inputValue);
        const nameParts = parse(item.name, nameMatches);

        return (
            <div>
                {nameParts.map((namePart, index) => (
                    <span key={index} className={namePart.highlight ? classes.highlight : ''}>
                        {namePart.text}
                    </span>
                ))}
            </div>
        );
    };

    const handleTagsOpen = () => {
        if (tags.length === 0) {
            dispatch(TagsActions.fetchTags());
        }
    };

    return (
        <Formik
            onSubmit={() => Promise.resolve()}
            initialValues={formValues}
            validationSchema={supplierDetailOverviewValidationSchema}
            enableReinitialize={true}
            initialErrors={errors}
            initialTouched={touched}
            innerRef={formikRef}
        >
            {formikProps => (
                <div className={classes.container}>
                    <PaloDirtyComparison<SupplierDetailOverviewFormValues>
                        baseline={withoutAddresses(baseline)}
                        normalize={withoutAddresses}
                    />
                    <SupplierDetailOverviewBasicInfos>
                        <PaloAccountHead
                            onShowAddresses={showAddresses}
                            parentField={
                                <Field name={fieldNames.parent}>
                                    {({ field, form }: FieldProps<{ id: number; name: string }>) => (
                                        <ParentSupplierAutocompleteList
                                            id="parent"
                                            label={<></>}
                                            required={false}
                                            onSelected={selected => form.setFieldValue(field.name, selected)}
                                            supplierId={supplier?.id}
                                            {...field}
                                        />
                                    )}
                                </Field>
                            }
                        />
                    </SupplierDetailOverviewBasicInfos>

                    <ScrollToFieldError />
                    {/* Two equal columns under the head, as in the template of
                        #2261: a wide table inside must not squeeze its neighbour. */}
                    <Box sx={{ containerType: 'inline-size' }}>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr)',
                                columnGap: '20px',
                                // Side by side only where both tables fit: below,
                                // the bank data scrolled sideways (R-205).
                                '@container (min-width: 1100px)': {
                                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                                },
                            }}
                        >
                            <PaloFormSection title={<FormattedMessage id="app.common.bankData" />}>
                                <SupplierDetailOverviewBankAccounts />
                            </PaloFormSection>
                            <PaloFormSection
                                title={<FormattedMessage id="app.contactsAndOrganizations.detail.notifications" />}
                                action={
                                    <Field name={fieldNames.forwardPurchaseOrderXmlToLobster}>
                                        {({ field, form }: FieldProps<boolean>) => (
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        color="primary"
                                                        checked={field.value}
                                                        onBlur={field.onBlur}
                                                        onChange={() => form.setFieldValue(field.name, !field.value)}
                                                        name={field.name}
                                                    />
                                                }
                                                label={
                                                    <PaloSwitchLabel>
                                                        <FormattedMessage id="app.label.forwardPurchaseOrderXmlToLobster" />
                                                    </PaloSwitchLabel>
                                                }
                                                sx={{ marginRight: 0 }}
                                            />
                                        )}
                                    </Field>
                                }
                            >
                                <Stack gap={1.5}>
                                    <PaloSectionText id="app.contactsAndOrganizations.detail.notifications.text" />
                                    <SupplierDetailOverviewNotifications />
                                </Stack>
                            </PaloFormSection>
                            <PaloFormSection
                                title={
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.classificationAndTags" />
                                }
                            >
                                <Stack gap={1.5}>
                                    <Field name={fieldNames.classifications}>
                                        {({ field }: FieldProps<Classification[]>) => (
                                            <ClassificationAutocomplete
                                                value={field.value}
                                                onChange={handleClassificationChange(formikProps)}
                                            />
                                        )}
                                    </Field>
                                    <Field name={fieldNames.tags}>
                                        {({ field }: FieldProps<Tag[]>) => (
                                            <ChipAutocomplete
                                                selectAll={true}
                                                noOptionsText={<FormattedMessage id="app.common.emptyResult" />}
                                                options={tags}
                                                getOptionLabel={tag => tag.name}
                                                renderOption={renderOption}
                                                getChipLabel={tag => tag.name}
                                                onChange={handleTagsChange(formikProps)}
                                                value={field.value}
                                                textfieldPlaceholder="app.common.addTag"
                                                onOpen={handleTagsOpen}
                                            />
                                        )}
                                    </Field>
                                </Stack>
                            </PaloFormSection>
                            <PaloFormSection
                                title={
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.internalNotes.title" />
                                }
                            >
                                <Stack gap={1.5}>
                                    <PaloSectionText id="app.contactsAndOrganizations.detail.internalNotes.text" />
                                    {supplier?.id && (
                                        <InternalNotes noteType={NoteType.Suppliers} entityId={supplier.id} />
                                    )}
                                </Stack>
                            </PaloFormSection>
                        </Box>
                    </Box>

                    <PaloFormSection
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.internalNotes" />}
                        collapsible={true}
                    >
                        <Field name={fieldNames.internalNotes}>
                            {({ field }: FieldProps<string>) => (
                                <>
                                    <PoolTextArea
                                        placeholder={intl.formatMessage({ id: 'app.label.description' })}
                                        rowsMax="50"
                                        rows="3"
                                        fullWidth={true}
                                        {...field}
                                    />
                                    <PaloLinkRow text={field.value} />
                                </>
                            )}
                        </Field>
                    </PaloFormSection>

                    {/* The standard mail and phone stand in the head; further
                        ones are kept here (#2326 R-245). */}
                    <PaloFormSection
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.allEmailsAndPhones" />}
                        collapsible={true}
                        defaultOpen={false}
                    >
                        <EmailTable withMain={false} />
                        <SectionDivider />
                        <PhonesTable withMain={false} />
                    </PaloFormSection>

                    <SupplierOverviewDetailSectionFtp
                        supplier={supplier}
                        formikProps={formikProps}
                        onDeleteFtpUser={() => dispatch(SupplierDetailOverviewActions.deleteFtpUser(supplier?.id))}
                        onSaveNewFtpPassword={() =>
                            dispatch(
                                SupplierDetailOverviewActions.saveNewFtpPassword(
                                    formikProps.values.newFtpPassword ?? '',
                                    supplier?.id
                                )
                            )
                        }
                        onAddFtp={() =>
                            dispatch(
                                SupplierDetailOverviewActions.addFtp(formikProps.values.ftpPassword ?? '', supplier?.id)
                            )
                        }
                    />

                    <PaloFormSection title={<FormattedMessage id="app.common.customers" />}>
                        <SupplierOverviewWhitelist formikProps={formikProps} />
                    </PaloFormSection>

                    <PaloFormSection title={<FormattedMessage id="app.label.categories" />} collapsible={true}>
                        <SupplierDetailOverviewCategories />
                    </PaloFormSection>

                    <PoolModal
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteTags" />}
                        handleClose={() => setShowTagsDeleteModal(false)}
                        isVisible={showTagsDeleteModal}
                        actionChildren={getTagsDeleteModalActionChildren(formikProps)}
                        hasModalTypography={true}
                    >
                        <PoolTypography id="app.contactsAndOrganizations.detail.deleteTagsMessage" />
                    </PoolModal>

                    <PoolModal
                        title={<FormattedMessage id="app.contactsAndOrganizations.detail.deleteClassification" />}
                        handleClose={() => setShowClassificationDeleteModal(false)}
                        isVisible={showClassificationDeleteModal}
                        actionChildren={getClassificationsModalActionChildren(formikProps)}
                        hasModalTypography={true}
                    >
                        <PoolTypography id="app.contactsAndOrganizations.detail.deleteClassificationMessage" />
                    </PoolModal>
                </div>
            )}
        </Formik>
    );
});
